/** Таблица регионов «сейчас» (агрегаты бэкенда по правилу 3). Порядок — как отдаёт бэкенд:
 *  6 макрорегионов по спросу, страны — свёрнутой группой «Зарубеж» (цифры строки группы — агрегат
 *  бэкенда `abroad` по тому же правилу 3), «Регион не определён» —
 *  всегда видимой строкой. Класса у агрегата нет и дни не красятся: дефицит виден счётчиками
 *  строк витрины по `deficit_class`. Заморожено — отдельным справочным столбцом. Клик по строке —
 *  фокус матрицы на регионе. */
import { ChevronDown, ChevronRight } from "lucide-react";
import React from "react";

import type { WbAggregate, WbRegionRow, WbRegions } from "@/api/wbRegions";
import { cn } from "@/lib/utils";
import { tdNum, thClass } from "../ozon-analytics/ui";
import {
  Confidence,
  Days,
  DeficitCountsView,
  PerDay,
  Qty,
} from "./cells";
import { confidenceTitle, datesText } from "./format";
import { columnKey, groupRegions, regionLabel, sum2 } from "./matrix";
import { ABROAD } from "./state";


function AggCells({ a }: { a: WbAggregate }): React.ReactElement {
  const sub = "text-[11px] leading-3 text-muted-foreground";
  return (
    <>
      <td className={tdNum}>
        <div className="leading-4">
          <PerDay v={a.orders_28d} />
          {a.demand_incomplete > 0 && (
            <span
              className="ml-1 cursor-help text-amber-600 dark:text-amber-400"
              title={`День неполный: ${a.demand_incomplete} строк с orders_complete = false — спрос и дни не взяты`}
              data-testid="incomplete-day"
            >
              ⚠
            </span>
          )}
        </div>
        <div className={sub}>
          FBO <PerDay v={a.orders_fbo_28d} /> · FBS <PerDay v={a.orders_fbs_28d} />
        </div>
      </td>
      <td className={tdNum}>
        <div className="leading-4" data-testid="fbo-reliable">
          <Qty v={sum2(a.fbo_stock_known_chain, a.fbo_stock_by_supply_signal)} />
        </div>
        <div className={sub}>
          всего <Qty v={a.fbo_stock} />
        </div>
      </td>
      <td className={tdNum} data-testid="fbo-weak">
        <Qty v={sum2(a.fbo_stock_weak, a.fbo_stock_unresolved)} />
      </td>
      <td className={tdNum}>
        <Confidence v={a.confidence} title={confidenceTitle(a)} />
      </td>
      <td className={tdNum} data-testid="days-fbo">
        <Days v={a.cover_days_fbo} demand={a.orders_28d} />
      </td>
      <td className={tdNum} data-testid="fbs">
        <Qty v={a.fbs_stock} />
      </td>
      <td className={tdNum} data-testid="days-total">
        <Days v={a.cover_days_total} demand={a.orders_28d} />
      </td>
      <td className="px-2 py-1 text-right">
        <DeficitCountsView counts={a.deficit_counts} />
      </td>
      <td className={cn(tdNum, "border-l bg-muted/30")} data-testid="frozen">
        <div className="leading-4">
          <Qty v={a.frozen_stock} />
        </div>
        <div className={sub}>
          исчезн. склад <Qty v={a.vanished_stock} />
        </div>
      </td>
    </>
  );
}

const COLS = 10;

export function RegionsTable({
  data,
  focus,
  onFocus,
  abroadOpen,
  onToggleAbroad,
}: {
  data: WbRegions;
  focus: string | null;
  onFocus: (key: string | null) => void;
  abroadOpen: boolean;
  onToggleAbroad: () => void;
}): React.ReactElement {
  const g = groupRegions(data.rows);
  const fbsDate = datesText(data.dates, "fbs_snapshot_date");
  const fboDate = datesText(data.dates, "fbo_stock_date");
  const frozenDate = datesText(data.dates, "frozen_stock_date");

  const regionRow = (r: WbRegionRow, indent = false): React.ReactElement => {
    const key = columnKey(r);
    const focused = key !== null && key === focus && r.region_kind !== "country";
    return (
      <tr
        key={`${r.region_kind}:${r.region_wb ?? ""}`}
        data-testid="region-row"
        aria-selected={focused}
        className={cn("border-t hover:bg-muted/40", focused && "bg-primary/10")}
      >
        <td className="px-2 py-1 text-left">
          <button
            type="button"
            className={cn("text-left hover:underline", indent && "pl-5", focused && "font-semibold")}
            title={focused ? "Снять фокус матрицы" : "Сортировать матрицу по этому региону"}
            onClick={() => onFocus(focused ? null : key)}
          >
            {regionLabel(r)}
          </button>
        </td>
        <AggCells a={r} />
      </tr>
    );
  };

  return (
    <div className="overflow-x-auto rounded-md border" data-testid="regions-table">
      <table className="w-full text-xs">
        <thead className="bg-muted/40">
          <tr>
            <th className={cn(thClass, "text-left")}>Регион</th>
            <th className={thClass} title="Заказов в день, среднее за 28 дней (FBO · FBS)">
              Спрос/день, 28 дн.
            </th>
            <th className={thClass} title="Склад виден / по цепочке + по сигналам поставки">
              FBO надёжно, шт.
            </th>
            <th className={thClass} title="attr19, боковые строки (слабо) + склад не определён">
              FBO слабо / не опр.
            </th>
            <th className={thClass} title="Σ штук × уверенность / Σ FBO">
              Уверенность
            </th>
            <th className={thClass}>Дни FBO</th>
            <th className={thClass} title={`Регион нашего склада, отгружает в любой регион; снимок ${fbsDate}`}>
              FBS*, шт.
            </th>
            <th className={thClass} title="(FBO + FBS) / спрос в день">
              Дни FBO+FBS
            </th>
            <th className={thClass} title="Строки оффер × регион по классу дефицита витрины">
              Офферы по классу
            </th>
            <th className={cn(thClass, "border-l")} title={`Справочно, снимок ${frozenDate}; не входит в FBO и дни`}>
              Заморожено (справ.)
            </th>
          </tr>
        </thead>
        <tbody>
          {g.macro.map((r) => regionRow(r))}
          {g.countries.length > 0 && (
            <tr
              className={cn("border-t", focus === ABROAD && "bg-primary/10")}
              data-testid="abroad-group"
              aria-selected={focus === ABROAD}
            >
              <td className="px-2 py-1 text-left">
                <button
                  type="button"
                  aria-expanded={abroadOpen}
                  className="inline-flex items-center gap-1 hover:underline"
                  title={g.countries.map(regionLabel).join(", ")}
                  onClick={onToggleAbroad}
                >
                  {abroadOpen ? (
                    <ChevronDown className="size-3.5" aria-hidden />
                  ) : (
                    <ChevronRight className="size-3.5" aria-hidden />
                  )}
                  Зарубеж — {g.countries.length} стран
                </button>
                <button
                  type="button"
                  className="block pl-5 text-[11px] text-muted-foreground hover:underline"
                  onClick={() => onFocus(focus === ABROAD ? null : ABROAD)}
                >
                  {focus === ABROAD ? "снять фокус матрицы" : "сортировать матрицу по странам"}
                </button>
              </td>
              {data.abroad ? (
                <AggCells a={data.abroad} />
              ) : (
                <td className="px-2 py-1 text-red-700 dark:text-red-400" colSpan={COLS - 1} data-testid="abroad-missing">
                  бэкенд не отдал агрегат «Зарубеж» при {g.countries.length} странах — цифры по каждой стране, раскройте
                </td>
              )}
            </tr>
          )}
          {abroadOpen && g.countries.map((r) => regionRow(r, true))}
          {g.unknown.length > 0 ? (
            g.unknown.map((r) => regionRow(r))
          ) : (
            <tr className="border-t" data-testid="unknown-empty">
              <td className="px-2 py-1 text-left">Регион не определён</td>
              <td className="px-2 py-1 text-muted-foreground" colSpan={COLS - 1}>
                строк нет: весь остаток и спрос — с определённым регионом
              </td>
            </tr>
          )}
          <tr className="border-t-2 font-medium" data-testid="total-row">
            <td className="px-2 py-1 text-left">
              <button
                type="button"
                className="hover:underline disabled:no-underline"
                disabled={focus === null}
                title={focus ? "Снять фокус матрицы" : undefined}
                onClick={() => onFocus(null)}
              >
                Итого
              </button>
            </td>
            <AggCells a={data.total} />
          </tr>
        </tbody>
      </table>
      <p className="border-t px-2 py-1.5 text-[11px] text-muted-foreground">
        Спрос, FBO и дни — на {fboDate}; * FBS — регион нашего склада, отгружает в любой регион,
        снимок на {fbsDate}. FBO — оценка: надёжная часть отдельно от слабой. Заморожено —
        справочно (снимок на {frozenDate}), в FBO и дни не входит.
      </p>
    </div>
  );
}
